import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FlaskConical, Plus, Trash2, ExternalLink, Loader2, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { createTestOrder, getTestOrders, deleteTestOrder } from '@/lib/api';
import { cn } from '@/lib/utils';

const AdminTestSimulatorPage = () => {
  const [testOrders, setTestOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [numItems, setNumItems] = useState(2);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => { loadOrders(); }, []);

  const loadOrders = async () => {
    try {
      const data = await getTestOrders();
      setTestOrders(data || []);
    } catch (e) { console.error(e); }
    setIsLoading(false);
  };

  const handleCreate = async () => {
    if (!testEmail) { toast.error('Inserisci un\'email di destinazione'); return; }
    setIsCreating(true);
    try {
      const result = await createTestOrder({ test_email: testEmail, num_items: numItems });
      toast.success(`Ordine test ${result.order_number} creato!`);
      await loadOrders();
    } catch (e) {
      toast.error('Errore nella creazione');
    }
    setIsCreating(false);
  };

  const handleDelete = async (orderNumber) => {
    try {
      await deleteTestOrder(orderNumber);
      toast.success('Ordine test eliminato');
      setTestOrders(prev => prev.filter(o => o.order_number !== orderNumber));
    } catch (e) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const portalUrl = window.location.origin.replace('/admindea26', '');

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
          <FlaskConical className="w-5 h-5 text-violet-600" />
        </div>
        <div>
          <h1 className="text-xl font-heading font-semibold text-gray-900">Simulatore Test</h1>
          <p className="text-sm text-gray-500">Crea ordini fittizi per testare il flusso completo (email incluse)</p>
        </div>
      </div>

      {/* Create new test order */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl border border-gray-200 p-6 mb-6"
      >
        <h2 className="font-semibold text-gray-900 mb-4">Crea Ordine Test</h2>
        <p className="text-sm text-gray-500 mb-5">
          L'ordine test ti permette di seguire l'intera procedura dal portale clienti. 
          Le email verranno inviate realmente, ma nessun ordine o voucher verrà creato su Shopify.
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-gray-600 block mb-1.5">Email di destinazione</label>
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="La tua email (riceverai le email test qui)"
              className="text-sm"
              data-testid="test-email-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1.5">Numero articoli</label>
            <Input
              type="number"
              min={1}
              max={5}
              value={numItems}
              onChange={(e) => setNumItems(parseInt(e.target.value) || 2)}
              className="text-sm"
              data-testid="test-items-input"
            />
          </div>
        </div>

        <Button
          onClick={handleCreate}
          disabled={isCreating || !testEmail}
          className="bg-violet-600 hover:bg-violet-700 text-white"
          data-testid="create-test-order-btn"
        >
          {isCreating ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Plus className="w-4 h-4 mr-2" />
          )}
          Crea Ordine Test
        </Button>
      </motion.div>

      {/* Test orders list */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Ordini Test Attivi</h2>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : testOrders.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <FlaskConical className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Nessun ordine test. Creane uno per iniziare.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {testOrders.map((order) => (
              <div key={order.order_number} className="px-6 py-4" data-testid={`test-order-${order.order_number}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-violet-700 font-mono">{order.order_number}</span>
                    <button
                      onClick={() => copyToClipboard(order.order_number)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Copia numero ordine"
                    >
                      {copiedId === order.order_number ? (
                        <Check className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(order.order_number)}
                    className="text-gray-400 hover:text-red-600 h-8"
                    data-testid={`delete-test-${order.order_number}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <div className="text-sm text-gray-600 mb-3 space-y-1">
                  <p>Email: <span className="font-medium text-gray-900">{order.email}</span></p>
                  <p>Articoli: {order.products?.length || 0} — Totale: EUR {order.total_price}</p>
                </div>

                {/* Instructions */}
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-violet-800 mb-2">Come testare:</p>
                  <ol className="text-xs text-violet-700 space-y-1.5 list-decimal list-inside">
                    <li>
                      Apri il{' '}
                      <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="underline font-medium inline-flex items-center gap-0.5">
                        portale clienti <ExternalLink className="w-3 h-3" />
                      </a>
                    </li>
                    <li>Inserisci come numero ordine: <code className="bg-white px-1.5 py-0.5 rounded border border-violet-200 font-mono font-bold">{order.order_number}</code></li>
                    <li>Inserisci come email: <code className="bg-white px-1.5 py-0.5 rounded border border-violet-200 font-mono">{order.email}</code></li>
                    <li>Segui la procedura normalmente (cambio, voucher o reso)</li>
                    <li>Riceverai le email reali all'indirizzo indicato</li>
                  </ol>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminTestSimulatorPage;
